import React, { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Clock, Video, AlertTriangle, Repeat } from "lucide-react";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { dateLocale } from "@/lib/i18n";
import { localeFor, TERMINAL_BOOKING_STATUSES, MOVABLE_BOOKING_STATUSES } from "./booking/calendarUtils";
import { formatMoneyIntl } from "../lib/formatMoney";
import RescheduleDialog from "./booking/RescheduleDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminHref } from "./adminPath";

const dtFmt = (d, lang) => {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toLocaleString(dateLocale(lang), { dateStyle: "medium", timeStyle: "short" });
};

// W4/B4 — the amount comes STRAIGHT off the booking (`payment_amount_cents` /
// `payment_currency`, frozen when the seat hold was created, cf. schemas.py) and is never
// recomputed from a transaction: this is what the buyer ACTUALLY paid. Same pattern as
// `fmtPrice` (Reserver.jsx), through the one money module — no invented currency.
const fmtAmount = (cents, currency, lang) => formatMoneyIntl(cents, currency, lang);

// `crypto.randomUUID` exists ONLY in a secure context (https / localhost) — same guard as
// StripeTransactions.jsx: an insecure context must not kill the refund, only weaken the
// nonce.
const requestId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* insecure context */ }
  return `rid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const STATUS_COLORS = {
  pending: "bg-eh-ochre/20 text-eh-ochre-deep",
  confirmed: "bg-eh-sage/30 text-eh-sage-deep",
  cancelled: "bg-red-100 text-red-700",
  // W4/B4 — money in flight (pending_payment) or money captured with no seat
  // (paid_needs_attention, a human decision required): distinct from the three historic
  // statuses above, and never rendered as a bare string with no colour. Amber, not red —
  // same palette as ConnectGateBanner.jsx: "needs attention" is not an error or a
  // failure (red), it is a human action still pending.
  pending_payment: "bg-amber-100 text-amber-800",
  paid_needs_attention: "bg-amber-200 text-amber-900",
  // F-25 — the SECOND terminal status, and the first that is a success: a per-minute
  // consultation whose money is settled. Slate, like every "this is history now" pill in
  // the product: it is neither an action waiting (amber) nor a failure (red).
  completed: "bg-slate-100 text-slate-600",
};

export default function BookingsAdmin() {
  const t = useT("admin");
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/admin/bookings");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // E2E finding — an appointment cancelled after a refund still rendered only
  // "120.00 $ · Cancelled": nothing distinguished money NEVER refunded from money already
  // returned (the practitioner can also refund from StripeTransactions.jsx, outside this
  // flow). One request, failure-tolerant — it is an informational badge, never a blocking
  // guard (same pattern as StripeTransactions.jsx).
  const [txMap, setTxMap] = useState({});
  const loadTx = async () => {
    try {
      // BUG-059 — the route now answers ONE PAGE plus its total
      // (`{rows, total, page, page_size}`), and this join needs every transaction that
      // could belong to a booking on screen, so it asks for the largest page the
      // endpoint serves. That is the same 500-row bound this call has always had, now
      // stated instead of being a server-side default. A practice past 500 payments
      // still loses the badge on its oldest bookings — the real fix is to query by the
      // session ids this page holds, which is a change to this component's load
      // sequencing rather than to the payments module.
      const { data } = await api.get("/admin/transactions", { params: { page_size: 500 } });
      const m = {};
      (data?.rows || []).forEach((tx) => {
        if (tx.session_id) {
          m[tx.session_id] = {
            refund_status: tx.refund_status,
            refunded_cents: tx.refunded_cents,
            payment_status: tx.payment_status,
            amount_cents: tx.amount_cents,
            currency: tx.currency,
            // W5/H2-08 — the DURABLE refund debt, written by the backend on the
            // transaction row (modules/payments/service.record_refund_obligation) and
            // discharged by the refund itself (_close_refund_obligation). It arrives
            // here for free: list_admin_transactions projects the whole document.
            refund_obligation: tx.refund_obligation || null,
          };
        }
      });
      setTxMap(m);
    } catch { /* no badge, never a blocked page */ }
  };
  useEffect(() => { loadTx(); }, []);
  const txFor = (b) => (b.payment_session_id ? txMap[b.payment_session_id] : null) || null;
  const isRefunded = (b) => {
    const info = txFor(b);
    return info?.refund_status === "refunded" || info?.refund_status === "partial";
  };
  const isPaid = (b) => txFor(b)?.payment_status === "paid";
  // The debt, read from the store — never recomputed here. Absent field (backend route
  // not yet deployed, historical row) → no chip, exactly as before.
  const refundOwed = (b) => {
    const ob = txFor(b)?.refund_obligation;
    return ob && ob.state === "owed" ? ob : null;
  };

  // Hold countdown — `payment_expires_at` moves in real time; a light tick every ~30s is
  // enough (a several-minute deadline does not need a per-second clock), cleared on
  // unmount.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const minutesRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    return Math.ceil((new Date(expiresAt).getTime() - now) / 60000);
  };

  // Double-click guard: two concurrent PUTs on the same booking used to release its time
  // twice server-side (fixed server-side, kept here as well).
  const [actingId, setActingId] = useState(null);
  const setStatus = async (id, status) => {
    if (actingId) return;
    setActingId(id);
    try {
      await api.put(`/admin/bookings/${id}`, { status });
      await load();
      toast.success(t("bookings.statusUpdated"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Restoring a cancelled or paid_needs_attention appointment re-claims its time
      // first; somebody else may hold it by now, and nothing overrides two clients on one
      // minute.
      if (detail === "TIME_UNAVAILABLE") toast.error(t("bookings.timeTaken"));
      // B3 — confirming or demoting a seat hold (pending_payment), or a paid appointment
      // with no seat (paid_needs_attention -> pending): an explicit refusal carrying a bare
      // code (cf. calendarUtils.ERROR_CODE_KEYS.PAYMENT_PENDING).
      else if (detail === "PAYMENT_PENDING") toast.error(t("err.paymentPending"));
      // A per-minute consultation that has already been charged: cancelling it would
      // promise the client a refund nothing tracks (the gateway owns that money), so the
      // server refuses and the practitioner is pointed at Payments.
      else if (detail === "ALREADY_COMPLETED") toast.error(t("err.consultCharged"));
      else toast.error(extractApiError(e, t("bookings.statusError")));
    } finally {
      setActingId(null);
    }
  };

  // ── B4 — cancelling a PAID appointment: warn first, offer the refund afterwards ──
  // `cancelPaidTarget` holds the booking awaiting confirmation in the warning dialog;
  // `refundTarget` holds the one JUST cancelled, awaiting a "Refund now" click (or being
  // dismissed — the refund stays a separate action, never an automatic one).
  const [cancelPaidTarget, setCancelPaidTarget] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(null);

  // N8 — the practitioner's own move to another time (RescheduleDialog.jsx). Distinct
  // from setStatus/confirm/cancel above: it changes WHEN the appointment is, never its
  // status.
  const [rescheduleTarget, setRescheduleTarget] = useState(null);

  const recordObligation = async (b) => {
    try {
      await api.post(`/admin/transactions/${b.payment_session_id}/refund-obligation`, {
        reason: "Booking cancelled by admin",
      });
      await loadTx();
    } catch (e) {
      // The cancel SUCCEEDED and the debt did not get written: the one state where a
      // silent failure costs the client their money. Say it plainly and keep the offer
      // on screen — the immediate refund is now the only path that closes it.
      toast.error(extractApiError(e, t("bookings.refundOwedNotRecorded", null,
        "Appointment cancelled, but the refund owed could not be recorded. Refund now.")));
    }
  };

  const confirmCancelPaid = async () => {
    const b = cancelPaidTarget;
    if (!b || actingId) return;
    setCancelPaidTarget(null);
    setActingId(b.id);
    try {
      await api.put(`/admin/bookings/${b.id}`, { status: "cancelled" });
      await load();
      toast.success(t("bookings.statusUpdated"));
      // Already refunded (e.g. from StripeTransactions.jsx, outside this flow): do not
      // offer a second refund on money that has already been returned.
      if (!isRefunded(b)) {
        // W5/H2-08 — the debt is recorded BEFORE the dialog opens, not when the
        // practitioner picks an answer. The seat is already released and the client is
        // already charged: the money is owed from this instant, whatever happens to this
        // tab next. "Later" used to be a dismissal that wrote nothing anywhere — no
        // chip, no queue, no flag — and closing the tab lost the offer entirely.
        // Idempotent server-side, so a re-cancel does not restart the attempt counter.
        await recordObligation(b);
        setRefundTarget(b);
      }
    } catch (e) {
      // Same refusal as in `setStatus`: a captured per-minute consultation is refunded,
      // never cancelled, and the raw code would say nothing to the practitioner.
      const detail = e?.response?.data?.detail;
      if (detail === "ALREADY_COMPLETED") toast.error(t("err.consultCharged"));
      else toast.error(extractApiError(e, t("bookings.statusError")));
    } finally {
      setActingId(null);
    }
  };

  // Shared by the follow-up dialog and by the per-row "Refund now" the owed chip carries.
  // A settled refund closes the obligation server-side (payments/service.py:
  // refund_transaction_for_tenant → _close_refund_obligation), so the chip disappears
  // because the DEBT is gone, not because a component unmounted.
  const doRefund = async (b) => {
    if (!b?.payment_session_id) return;
    setRefunding(b.id);
    try {
      await api.post(`/admin/transactions/${b.payment_session_id}/refund`, {
        reason: "Booking cancelled by admin",
        client_request_id: requestId(),
      });
      toast.success(t("bookings.refundSuccess"));
      setRefundTarget(null);
      await loadTx();
    } catch (e) {
      toast.error(extractApiError(e, t("bookings.refundError")));
    } finally {
      setRefunding(null);
    }
  };

  // Telehealth is per-minute consults and nothing else: a fixed-price booking is
  // `in_person` by product nature (platform_helpers.py), so there is no hand-opened room
  // to mint any more and `POST /telehealth/rooms` is gone. A per-minute booking's room
  // already exists — `consults._insert_room` writes it when the authorisation intent is
  // created, long before this row renders — so the action is pure navigation to OUR OWN
  // room page (admin/telehealth/ConsultRoomPage.jsx), which hosts the call and
  // heartbeats for the whole visit rather than handing the browser to the provider.
  const openConsultRoom = (b) => {
    window.open(adminHref(`/admin/telehealth/consult/${b.id}`), "_blank");
  };

  // "Refund owed" is a filter over the transaction, not over `booking.status` — the rows
  // it collects are `cancelled`, i.e. the tab where a debt is least likely to be looked
  // for. It is deliberately NOT folded into `paid_needs_attention`: that one is a real
  // backend status ("paid, no seat"), and two different facts under one label is how
  // this page got into trouble in the first place.
  const owedList = items.filter((b) => !!refundOwed(b));
  const list = filter === "all"
    ? items
    : filter === "refund_owed"
      ? owedList
      : items.filter((b) => b.status === filter);

  return (
    <div data-testid="admin-bookings">
      <h1 className="font-display text-4xl text-eh-ink">{t("bookings.title")}</h1>
      <p className="text-eh-muted mt-1">{t("bookings.subtitle")}</p>

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        {[
          { v: "all", l: t("bookings.all") },
          { v: "pending", l: t("bookings.pending") },
          { v: "confirmed", l: t("bookings.confirmed") },
          { v: "cancelled", l: t("bookings.cancelled") },
          { v: "pending_payment", l: t("bookings.pendingPayment"), testid: "bookings-tab-pending_payment" },
          { v: "paid_needs_attention", l: t("bookings.paidNeedsAttention"), testid: "bookings-tab-paid_needs_attention" },
          {
            v: "refund_owed",
            l: `${t("bookings.refundOwedTab", null, "Refund owed")} (${owedList.length})`,
            testid: "bookings-tab-refund_owed",
            urgent: owedList.length > 0,
          },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              filter === f.v
                ? "bg-eh-ink text-eh-bg border-eh-ink"
                : f.urgent
                  ? "bg-rose-100 text-rose-800 border-rose-300 font-medium hover:bg-rose-200"
                  : "border-eh-border-soft text-eh-muted hover:bg-eh-cream"
            }`}
            data-testid={f.testid || `bookings-filter-${f.v}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-bookings-list">
        {list.length === 0 && (
          <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-8 text-center">
            {t("bookings.empty")}
          </div>
        )}
        {list.map((b) => {
          // BUG-015b — "PAID" HERE MEANS CAPTURED, not "a checkout was once started".
          // `payment_session_id` is stamped when the Stripe session is CREATED, so a
          // booking whose client opened the payment page and never entered a card carried
          // it too. Confirmed by hand afterwards, that booking hit the "this appointment
          // was paid — cancelling does not refund it" dialog and then the "Refund now"
          // offer, over money that never arrived: a refund attempt on an uncaptured
          // session, and a practitioner told they owe something they were never paid.
          // The MONEY FACT has two witnesses and either is enough: the transaction
          // (`payment_status === "paid"`, absent while the tx store has not loaded) and
          // the capture stamped on the booking document itself.
          // Already refunded (elsewhere, e.g. StripeTransactions.jsx): a plain cancel is
          // enough — no warning about unrefunded money, no "Refund now" follow-up.
          const isPaidCaptured = !!b.payment_session_id && b.status === "confirmed"
            && (isPaid(b) || !!b.payment_captured_at) && !isRefunded(b);
          const owed = refundOwed(b);
          // Only a per-minute consult HAS a room: that is the one telehealth model left,
          // and its room is minted with the authorisation. A fixed-price booking showing
          // this button would link to a consult page for an appointment that has no
          // session at all.
          //
          // H2-06 — a `pending_payment` booking (checkout started, card never entered)
          // carried a fully working "start video consultation" button, indistinguishable
          // from a paid one: one click and the practitioner delivers the session for
          // free. That status gate stays, on top of the per-minute one.
          //
          // GATED ON DELIVERY, NOT ON BILLING. `delivery_mode` is what says this
          // appointment has a room; `consult_pricing_mode` says how it is charged, and it
          // now answers "per_minute" for only one of the two telehealth models. Reading
          // the billing field here hid the video button on every fixed-block
          // consultation the practice sold.
          const canStartVideo = b.delivery_mode === "video"
            && (b.status === "confirmed" || b.status === "pending");
          return (
          <div key={b.id} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-5">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
              <div className="md:col-span-3">
                <div className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("bookings.slot")}</div>
                <div className="font-medium text-eh-ink mt-1 flex items-center gap-1.5"><Clock size={12} /> {dtFmt(b.slot_start, lang)}</div>
                <div className="text-xs text-eh-muted">{b.service}</div>
                {/* H2-19 — the amount alone said nothing about whether the money
                    arrived: a paid and an unpaid `confirmed` booking rendered an
                    identical "$120.00". The paid/unpaid word comes from the
                    TRANSACTION (`payment_status`), never from the catalogue price. */}
                {!!b.payment_amount_cents && (
                  <div className="text-xs mt-1 flex items-center gap-1.5" data-testid={`booking-amount-${b.id}`}>
                    <span className="font-medium text-eh-ink">{fmtAmount(b.payment_amount_cents, b.payment_currency, lang)}</span>
                    {isPaid(b) ? (
                      <span className="px-1.5 py-0.5 rounded-full bg-eh-sage/30 text-eh-sage-deep" data-testid={`booking-paid-${b.id}`}>
                        {t("bookings.paidChip", null, "paid")}
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-full bg-eh-border-soft text-eh-muted" data-testid={`booking-unpaid-${b.id}`}>
                        {t("bookings.unpaidChip", null, "not paid")}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="md:col-span-4">
                <div className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("bookings.client")}</div>
                <div className="font-medium text-eh-ink mt-1">{b.firstName} {b.lastName}</div>
                <div className="text-xs text-eh-muted break-all">{b.email}</div>
                {b.phone && <div className="text-xs text-eh-muted">{b.phone}</div>}
              </div>
              <div className="md:col-span-3 text-sm text-eh-ink/80 line-clamp-3">{b.notes || "—"}</div>
              <div className="md:col-span-2 flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_COLORS[b.status] || ""}`}>{t(`bookings.statusLabel.${b.status}`, null, b.status)}</span>
                  {b.status === "pending_payment" && (
                    <span
                      className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid={`booking-awaiting-${b.id}`}
                    >
                      {t("bookings.awaitingPayment")}
                      {b.payment_expires_at && (() => {
                        const mins = minutesRemaining(b.payment_expires_at);
                        return (
                          <span data-testid={`booking-expires-${b.id}`}>
                            {" · "}{mins > 0 ? t("bookings.expiresIn", { min: mins }) : t("bookings.expired")}
                          </span>
                        );
                      })()}
                    </span>
                  )}
                  {isRefunded(b) && (
                    <span
                      className="text-xs px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200"
                      data-testid={`booking-refunded-${b.id}`}
                    >
                      {t("stx.refunded")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canStartVideo && (
                    <button
                      onClick={() => openConsultRoom(b)}
                      className="p-2 rounded-full bg-sol-gold/20 hover:bg-sol-gold/30 text-sol-gold-deep"
                      title={t("bookings.startVideo")}
                      data-testid={`start-video-${b.id}`}
                    >
                      <Video size={14} />
                    </button>
                  )}
                  {/* B3 — confirmer un maintien de place (pending_payment) mintait un
                      rendez-vous GRATUIT (l'argent n'était jamais réclamé) ; confirmer
                      paid_needs_attention vit dans le bandeau ci-dessous ("Conserver"),
                      pas ici, pour ne jamais laisser deux boutons faire la même chose. */}
                  {["pending", "cancelled"].includes(b.status) && (
                    <button onClick={() => setStatus(b.id, "confirmed")} disabled={!!actingId} data-testid={`booking-confirm-${b.id}`} className="p-2 rounded-full bg-eh-sage/20 hover:bg-eh-sage/30 text-eh-sage-deep disabled:opacity-40" title={t("bookings.confirm")}>
                      <Check size={14} />
                    </button>
                  )}
                  {/* N8 — the practitioner's own move to another time, offered only in the
                      server's own movable states (calendarUtils.MOVABLE_BOOKING_STATUSES):
                      a booking with no live time to move never gets this button. */}
                  {MOVABLE_BOOKING_STATUSES.includes(b.status) && (
                    <button
                      onClick={() => setRescheduleTarget(b)}
                      disabled={!!actingId}
                      data-testid={`booking-reschedule-${b.id}`}
                      className="p-2 rounded-full bg-sky-50 hover:bg-sky-100 text-sky-700 disabled:opacity-40"
                      title={t("bookings.reschedule")}
                    >
                      <Repeat size={14} />
                    </button>
                  )}
                  {/* Cancelling a PAID appointment (confirmed + payment_session_id) goes
                      through the warning — never a direct click that keeps the money
                      silently and never refunds it. paid_needs_attention has NO such
                      button: its cancellation lives in the banner ("Refund and cancel").

                      TWO EXCLUSIONS, TWO REASONS (F-25 residue R2). The terminal statuses
                      are withheld because "cancel" is a statement that the appointment
                      WILL NOT HAPPEN, and neither `cancelled` nor `completed` has a
                      future to make it about — the server says the same thing with
                      `{"$nin": ["cancelled", "completed"]}` on the transition claim, and
                      a control that only ever earns a refusal is not a control. The set
                      is READ, not re-listed, so the next status added to the vocabulary
                      is classified once. `paid_needs_attention` is live, and is withheld
                      for the entirely different reason above. */}
                  {!TERMINAL_BOOKING_STATUSES.includes(b.status) && b.status !== "paid_needs_attention" && (
                    <button
                      onClick={() => (isPaidCaptured ? setCancelPaidTarget(b) : setStatus(b.id, "cancelled"))}
                      disabled={!!actingId}
                      data-testid={`booking-cancel-${b.id}`}
                      className="p-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-40"
                      title={t("bookings.cancel")}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* W5/H2-08 — the debt, rendered where the practitioner already works. Rose,
                not amber: `paid_needs_attention` is a decision waiting to be made,
                this is money the client is owed and has not been sent. It survives a
                reload because it is a field on the transaction, not dialog state. */}
            {owed && (
              <div className="mt-4 bg-rose-50 border border-rose-300 rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid={`booking-refund-owed-${b.id}`}>
                <div className="flex items-center gap-1.5 text-rose-900 text-sm font-medium">
                  <AlertTriangle size={14} />
                  {t("bookings.refundOwed", {
                    amount: fmtAmount(owed.owed_cents, txFor(b)?.currency || b.payment_currency, lang),
                  }, "Refund owed to the client — {amount}")}
                </div>
                {!!owed.last_error && (
                  <span className="text-xs text-rose-800/80" data-testid={`booking-refund-owed-error-${b.id}`}>
                    {t("bookings.refundOwedLastError", { error: owed.last_error }, "Last attempt failed: {error}")}
                  </span>
                )}
                <button
                  onClick={() => doRefund(b)}
                  disabled={refunding === b.id}
                  data-testid={`booking-refund-owed-now-${b.id}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-40"
                >
                  {refunding === b.id ? t("bookings.refundInProgress", null, "Refunding…") : t("bookings.refundNow")}
                </button>
              </div>
            )}

            {b.status === "paid_needs_attention" && (
              // Amber, not red — same palette as ConnectGateBanner.jsx: money captured
              // and awaiting a human decision is not a failure, it is a pending action.
              <div className="mt-4 bg-amber-50 border border-amber-300 rounded-xl p-4" data-testid={`booking-needs-attention-${b.id}`}>
                <div className="flex items-center gap-1.5 text-amber-900 text-sm font-medium">
                  <AlertTriangle size={14} /> {t("bookings.needsAttentionTitle")}
                </div>
                <p className="text-xs text-amber-800/80 mt-1">{t("bookings.needsAttentionBody")}</p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => { if (!actingId) setStatus(b.id, "confirmed"); }}
                    disabled={!!actingId}
                    data-testid={`booking-keep-${b.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-eh-sage/20 hover:bg-eh-sage/30 text-eh-sage-deep disabled:opacity-40"
                  >
                    {t("bookings.keepAppointment")}
                  </button>
                  <button
                    onClick={() => { if (!actingId) setCancelPaidTarget(b); }}
                    disabled={!!actingId}
                    data-testid={`booking-refund-cancel-${b.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-red-100 hover:bg-red-200 text-red-800 disabled:opacity-40"
                  >
                    {t("bookings.refundAndCancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      <AlertDialog open={!!cancelPaidTarget} onOpenChange={(open) => { if (!open) setCancelPaidTarget(null); }}>
        <AlertDialogContent data-testid="booking-cancel-paid-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("bookings.paidWarnTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelPaidTarget && t("bookings.paidWarnBody", {
                amount: fmtAmount(cancelPaidTarget.payment_amount_cents, cancelPaidTarget.payment_currency, lang),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="booking-cancel-paid-dismiss">{t("bookings.paidWarnDismiss")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelPaid} data-testid="booking-cancel-paid-confirm">{t("bookings.paidWarnConfirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!refundTarget} onOpenChange={(open) => { if (!open) setRefundTarget(null); }}>
        <AlertDialogContent data-testid="booking-refund-followup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("bookings.refundFollowupTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {refundTarget && t("bookings.refundFollowupBody", {
                amount: fmtAmount(refundTarget.payment_amount_cents, refundTarget.payment_currency, lang),
              })}
            </AlertDialogDescription>
            {/* "Later" is no longer a dismissal — say what it now does, or the practitioner
                has no reason to believe the debt survives the dialog. */}
            <p className="text-xs text-eh-muted" data-testid="booking-refund-later-explain">
              {t("bookings.refundLaterExplain", null,
                "Choose Later and this refund stays recorded as owed: the appointment keeps a \"Refund owed\" tag with a Refund now button, and it is listed under the \"Refund owed\" filter.")}
            </p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="booking-refund-later">{t("bookings.refundLater")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => doRefund(refundTarget)} disabled={!!refunding} data-testid="booking-refund-now">{t("bookings.refundNow")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {rescheduleTarget && (
        <RescheduleDialog
          booking={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
